import { isExtractionTransferReady } from './recognized-stage'
import type { ExtractionStage } from '../../../api/listeners/document-extraction'

const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const textualDate = new RegExp(`^(\\d{1,2}) (${months.join('|')}) (\\d{4})(?: ?(?:года|г\\.?))?$`, 'u')
const dateText = (value: string) => value.trim().replace(/\s+/gu, ' ').toLocaleLowerCase('ru')
export const isExtractedTextualDate = (value: string) => textualDate.test(dateText(value))
const unconfirmedLinks = new Set(['person_link_unconfirmed', 'document_link_unconfirmed'])

/** Calendar components only: interpreting a birthday as a UTC timestamp can change its day. */
export const normalizeExtractedBirthDate = (value: string, today = new Date()): string | null => {
    const text = dateText(value)
    const printed = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text)
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
    const words = textualDate.exec(text)
    if (!printed && !iso && !words) return null
    const [year, month, day] = printed ? [+printed[3], +printed[2], +printed[1]]
        : iso ? [+iso[1], +iso[2], +iso[3]] : [+words![3], months.indexOf(words![2]) + 1, +words![1]]
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
    const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    if (year < 100 || month < 1 || month > 12 || day < 1 || day > days[month - 1]) return null
    if (year * 10000 + month * 100 + day > today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate()) return null
    return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${String(year).padStart(4, '0')}`
}

export const recognizedBirthDates = (stage: ExtractionStage | null): { dates: string[]; needsReview: boolean } => {
    const dates: string[] = []
    let needsReview = false
    if (!stage || !isExtractionTransferReady(stage)) return { dates, needsReview }
    const numericAnchors: { date: string; source: string }[] = []
    const textualPending: { date: string; source: string }[] = []
    for (const [partIndex, part] of stage.parts.entries()) {
        if (part.status !== 'succeeded' || !part.result) continue
        for (const document of part.result.documents) {
            // The questionnaire birthday comes from the passport only. Other
            // document dates neither provide a fallback nor confirm a reading.
            if (document.type !== 'passport_main') continue
            const field = document.fields.find(item => item.name === 'birthDate')
            const rawText = field?.rawValue?.trim() || ''
            const normalizedText = field?.normalizedValue?.trim() || ''
            if (!field || (!rawText && !normalizedText)) continue
            const raw = rawText ? normalizeExtractedBirthDate(rawText) : null
            const normalized = normalizedText ? normalizeExtractedBirthDate(normalizedText) : null
            const date = normalized || raw
            const source = `${partIndex}:${document.documentId}`
            const isTextual = textualDate.test(dateText(rawText))
            const birthDateRef = (ref: { documentId: string; field: string }) => ref.documentId === document.documentId && ref.field === 'birthDate'
            const sourceConflicts = part.result.conflicts.filter(conflict => !unconfirmedLinks.has(conflict.code) && conflict.fields.some(birthDateRef))
            // A comparison with SNILS or an education document cannot disprove
            // a readable passport birthday. Preserve conflicts between passports
            // and references whose source cannot be established.
            const outsidePassport = (conflict: typeof sourceConflicts[number]) => conflict.code === 'birth_date_mismatch'
                && conflict.fields.every(ref => ref.field === 'birthDate'
                    && part.result!.documents.some(item => item.documentId === ref.documentId))
                && new Set(conflict.fields.filter(ref => part.result!.documents.some(item => item.documentId === ref.documentId && item.type === 'passport_main'))
                    .map(ref => ref.documentId)).size === 1
                && conflict.fields.some(ref => part.result!.documents.some(item => item.documentId === ref.documentId && item.type !== 'passport_main'))
            const conflicting = sourceConflicts.some(conflict => !outsidePassport(conflict))
            const unresolved = part.result.unresolved.filter(item => !unconfirmedLinks.has(item.code)
                && item.documentId === document.documentId && item.field === 'birthDate')
            const outsidePassportValidation = sourceConflicts.length > 0 && sourceConflicts.every(outsidePassport)
                && !unresolved.length
            const invalidValue = !date || field.candidates.length > 0 || (rawText && !raw)
                || (normalizedText && !normalized) || (raw && normalized && raw !== normalized)
                || (!outsidePassportValidation && ['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument))
            // LMS may erase normalizedValue and mark the field as conflicting
            // after comparing it with another document type. A fully evidenced
            // passport reading remains usable when that comparison is the only
            // reason for the conflict.
            const passportReading = !invalidValue && outsidePassportValidation && raw
                && field.status === 'conflict' && field.normalizedValue === null
                && field.validation.crossDocument === 'conflict' && field.evidence.length > 0
                && ['passed', 'not_checked'].includes(field.validation.format)
            // Older LMS results flag a fully printed textual month as ambiguous.
            // Recover only with evidence and a matching numeric date in another
            // passport; an ambiguous numeric reading never gets this exception.
            if (!invalidValue && isTextual && raw && field.status === 'ambiguous' && field.normalizedValue === null
                && ['failed', 'not_checked'].includes(field.validation.format) && field.evidence.length > 0
                && !conflicting && unresolved.every(item => item.code === 'ambiguous')) {
                textualPending.push({ date: raw, source })
                continue
            }
            if (invalidValue || (field.status !== 'extracted' && !passportReading)
                || ['failed', 'invalid', 'conflict'].includes(field.validation.format)) {
                needsReview = true
                continue
            }
            if (conflicting || unresolved.length) needsReview = true
            else if (raw && !isTextual) numericAnchors.push({ date: raw, source })
            if (!dates.includes(date!)) dates.push(date!)
        }
    }
    for (const pending of textualPending) {
        if (!numericAnchors.some(anchor => anchor.source !== pending.source && anchor.date === pending.date)) needsReview = true
    }
    return { dates, needsReview }
}
